import { useContext, useState, useEffect, useMemo } from 'react';
import { GlossaryContext } from '../../contexts/GlossaryContext';
import { useTooltipPosition } from '../../hooks/useTooltipPosition';
import type { GlossaryTermSummary } from '../../services/glossaryService';

interface PopoverProps {
  term: GlossaryTermSummary;
  x: number;
  y: number;
  onClose: () => void;
}

const GlossaryPopover: React.FC<PopoverProps> = ({ term, x, y, onClose }) => {
  const posStyle = useTooltipPosition(x, y);

  useEffect(() => {
    const handler = () => onClose();
    document.addEventListener('touchstart', handler);
    return () => document.removeEventListener('touchstart', handler);
  }, [onClose]);

  return (
    <div
      className="fixed z-50 bg-gray-900 border border-amber-500/40 rounded-lg shadow-xl p-3 max-w-xs"
      style={posStyle}
      data-testid="glossary-popover"
      onTouchStart={e => e.stopPropagation()}
    >
      <p className="text-amber-400 font-semibold text-sm mb-1">{term.term}</p>
      <p className="text-gray-300 text-xs leading-relaxed">
        {term.definition.slice(0, 150)}{term.definition.length > 150 ? '…' : ''}
      </p>
      <a
        href={`/knowledge/glossary/${term.slug}`}
        className="text-amber-400 text-xs mt-2 block hover:underline"
        onClick={onClose}
      >
        Read more →
      </a>
    </div>
  );
};

interface GlossaryTooltipProps {
  children: string;
}

export const GlossaryTooltip: React.FC<GlossaryTooltipProps> = ({ children: text }) => {
  const { terms, termRegex } = useContext(GlossaryContext);
  const [active, setActive] = useState<{ term: GlossaryTermSummary; x: number; y: number } | null>(null);

  const termMap = useMemo(
    () => new Map(terms.map(t => [t.term.toLowerCase(), t])),
    [terms],
  );

  if (!termRegex || !text) return <>{text}</>;

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  // Clone regex to reset lastIndex — global regex is stateful
  const regex = new RegExp(termRegex.source, termRegex.flags);
  regex.lastIndex = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const matched = match[0];
    const termData = termMap.get(matched.toLowerCase());
    if (termData) {
      parts.push(
        <span
          key={match.index}
          className="underline decoration-dotted decoration-amber-400 cursor-help"
          onMouseEnter={e => setActive({ term: termData, x: e.clientX, y: e.clientY })}
          onMouseLeave={() => setActive(null)}
          onTouchStart={e => {
            e.preventDefault();
            e.stopPropagation(); // prevent document handler from immediately closing the popover
            setActive({ term: termData, x: e.touches[0].clientX, y: e.touches[0].clientY });
          }}
          data-testid="glossary-term"
        >
          {matched}
        </span>,
      );
    } else {
      parts.push(matched);
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));

  return (
    <>
      {parts}
      {active && (
        <GlossaryPopover
          term={active.term}
          x={active.x}
          y={active.y}
          onClose={() => setActive(null)}
        />
      )}
    </>
  );
};
